// ─────────────────────────────────────────────────────────────────────────────
// AudienceRail, CD's streaming-stack rail beside the Spotify hero: the Amazon +
// Apple mini DSP cards and the 2x2 social mini cards (Instagram / Facebook /
// TikTok / YouTube). All values come from /api/proxy/stats (DashboardStats);
// every slot renders "-" when its field is absent (Bible 06.4). FB/TikTok/YT
// "last post" is NOT populated by the worker, so only Instagram shows a stamp.
// ─────────────────────────────────────────────────────────────────────────────

import type { JSX } from 'react';
import type { DashboardStats, AmazonStats, AppleStats } from '../../lib';
import { fmt } from './format';
import { BrandIcon, BRAND_ICON } from './glyphs';

export interface AudienceRailProps {
  readonly stats: DashboardStats | null;
}

/** Read a {current,total}|number sub-shape → { current, total }. */
function pair(v: number | { current?: number; total?: number } | undefined): {
  current: number | null;
  total: number | null;
} {
  if (v == null) return { current: null, total: null };
  if (typeof v === 'number') return { current: v, total: v };
  return {
    current: typeof v.current === 'number' ? v.current : null,
    total: typeof v.total === 'number' ? v.total : null,
  };
}

/** "9 / 9" style editorial label (current / total). */
function ratioLabel(v: number | { current?: number; total?: number } | undefined): string {
  const { current, total } = pair(v);
  if (current == null) return '-';
  return fmt(current) + ' / ' + fmt(total ?? current);
}

/** A relative "Last post · 3d ago" stamp from an ISO timestamp. */
function lastPostLabel(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  const days = Math.floor((Date.now() - d.getTime()) / 86400000);
  if (days <= 0) return 'Last post · today';
  return 'Last post · ' + days + 'd ago';
}

export function AudienceRail({ stats }: AudienceRailProps): JSX.Element {
  const amazon: AmazonStats | undefined = stats?.amazon_music;
  const apple: AppleStats | undefined = stats?.apple_music;
  const ig = stats?.instagram;
  const fb = stats?.facebook;
  const tt = stats?.tiktok;
  const yt = stats?.youtube;

  const amazonFollowers = amazon?.followers ?? null;
  const applePlaylists = apple?.playlists;
  const igFollowers = ig?.followers ?? null;
  const fbFollowers = fb?.followers ?? null;
  const ttFollowers = tt?.followers ?? null;
  const ytSubs = yt?.subscribers ?? null;
  const igLastPost = lastPostLabel(ig?.last_post_at);

  return (
    <div className="streaming-stack">
      {/* Amazon Music */}
      <article className="card platform-card platform-card--mini pc--amazon">
        <header className="platform-card__head">
          <span className="pc-icon pc-icon--brand">
            <BrandIcon file={BRAND_ICON.amazon} className="pc-icon__img pc-icon__img--zoom" />
          </span>
          <h4 className="platform-card__name">Amazon Music</h4>
        </header>
        <div className="pc-kpis pc-kpis--two">
          <div>
            <p className="kpi__label">Followers</p>
            <p className="kpi__value kpi__value--sm">
              {amazonFollowers != null && amazonFollowers > 0 ? fmt(amazonFollowers) : '-'}
            </p>
          </div>
          <div>
            <p className="kpi__label">Editorial</p>
            <p className="kpi__value kpi__value--sm">{ratioLabel(amazon?.editorial_playlists)}</p>
          </div>
        </div>
      </article>

      {/* Apple Music */}
      <article className="card platform-card platform-card--mini pc--apple">
        <header className="platform-card__head">
          <span className="pc-icon pc-icon--brand">
            <BrandIcon file={BRAND_ICON.apple} className="pc-icon__img pc-icon__img--zoom" />
          </span>
          <h4 className="platform-card__name">Apple Music</h4>
        </header>
        <div className="pc-kpis pc-kpis--two">
          <div>
            <p className="kpi__label">Current</p>
            <p className="kpi__value kpi__value--sm">
              {(() => {
                const c = pair(applePlaylists).current;
                return c != null ? fmt(c) : '-';
              })()}
            </p>
          </div>
          <div>
            <p className="kpi__label">Editorial</p>
            <p className="kpi__value kpi__value--sm">
              {(() => {
                const c = pair(apple?.editorial_playlists).current;
                return c != null ? fmt(c) : '-';
              })()}
            </p>
          </div>
        </div>
      </article>

      {/* Socials 2x2 */}
      <div className="socials-2x2">
        <article className="card platform-card platform-card--mini pc--instagram">
          <header className="platform-card__head">
            <span className="pc-icon pc-icon--brand">
              <BrandIcon file={BRAND_ICON.instagram} className="pc-icon__img" />
            </span>
            <h4 className="platform-card__name">Instagram</h4>
          </header>
          <div className="pc-kpis pc-kpis--one">
            <div>
              <p className="kpi__label">Followers</p>
              <p className="kpi__value kpi__value--sm">
                {igFollowers != null && igFollowers > 0 ? fmt(igFollowers) : '-'}
              </p>
            </div>
          </div>
          <p className="pc-lastpost">{igLastPost || 'No posts yet'}</p>
        </article>

        <article className="card platform-card platform-card--mini pc--facebook">
          <header className="platform-card__head">
            <span className="pc-icon pc-icon--brand">
              <BrandIcon file={BRAND_ICON.facebook} className="pc-icon__img" />
            </span>
            <h4 className="platform-card__name">Facebook</h4>
          </header>
          <div className="pc-kpis pc-kpis--one">
            <div>
              <p className="kpi__label">Followers</p>
              <p className="kpi__value kpi__value--sm">
                {fbFollowers != null && fbFollowers > 0 ? fmt(fbFollowers) : '-'}
              </p>
            </div>
          </div>
        </article>

        <article className="card platform-card platform-card--mini pc--tiktok">
          <header className="platform-card__head">
            <span className="pc-icon pc-icon--brand">
              <BrandIcon file={BRAND_ICON.tiktok} className="pc-icon__img" />
            </span>
            <h4 className="platform-card__name">TikTok</h4>
          </header>
          <div className="pc-kpis pc-kpis--one">
            <div>
              <p className="kpi__label">Followers</p>
              <p className="kpi__value kpi__value--sm">
                {ttFollowers != null && ttFollowers > 0 ? fmt(ttFollowers) : '-'}
              </p>
            </div>
          </div>
        </article>

        <article className="card platform-card platform-card--mini pc--youtube">
          <header className="platform-card__head">
            <span className="pc-icon pc-icon--brand">
              <BrandIcon file={BRAND_ICON.youtube} className="pc-icon__img pc-icon__img--bare" />
            </span>
            <h4 className="platform-card__name">YouTube</h4>
          </header>
          <div className="pc-kpis pc-kpis--one">
            <div>
              <p className="kpi__label">Subscribers</p>
              <p className="kpi__value kpi__value--sm">
                {ytSubs != null && ytSubs > 0 ? fmt(ytSubs) : '-'}
              </p>
            </div>
          </div>
        </article>
      </div>
    </div>
  );
}
